import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  List, Home, Settings, LogOut, UserCircle2, Trash2, KeyRound, ChevronDown, Repeat,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import ChangePinDialog from "./ChangePinDialog";

// Consult the JobModal's dirty-form guard (if registered on window).
// Returns true when it's safe to navigate; false when the user chose to
// stay in the form.
const confirmNavigation = (e) => {
  const guard = window.__vfmFormGuard;
  if (guard && !guard()) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
  return true;
};

export const Header = () => {
  const { user, logout, isAdmin } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [pinOpen, setPinOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const updateScroll = () => setScrolled(window.scrollY > 8);
    updateScroll();
    window.addEventListener("scroll", updateScroll, { passive: true });
    return () => window.removeEventListener("scroll", updateScroll);
  }, []);
  const isActive = (p) => loc.pathname === p || (p !== "/" && loc.pathname.startsWith(p));

  const handleSwitchUser = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const pinLabel = user?.pinMigrated ? "Byt PIN-kod" : "Byt till PIN-kod";

  return (
    <header data-testid="app-header"
            className={`vm-header ${scrolled ? "vm-header-scrolled" : ""} w-full px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between bg-transparent flex-wrap gap-3`}>
      <Logo onClick={() => {
        if (!confirmNavigation({ preventDefault() {}, stopPropagation() {} })) return;
        nav("/");
      }} />
      <nav className="flex items-center gap-1 sm:gap-2 flex-wrap">
        <Link to="/" data-testid="nav-home-btn" onClick={confirmNavigation}
              aria-label="Hem"
              className={`press-feedback px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center min-h-[40px] ${
                isActive("/") && loc.pathname === "/" ? "text-[#3B9EDA] bg-[#E6F1FB]" : "text-slate-600 hover:bg-slate-100"}`}>
          <Home className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Hem</span>
        </Link>
        <Link to="/arenden" data-testid="nav-jobs-btn" onClick={confirmNavigation}
              aria-label="Ärenden"
              className={`press-feedback px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center min-h-[40px] ${
                isActive("/arenden") ? "text-[#3B9EDA] bg-[#E6F1FB]" : "text-slate-600 hover:bg-slate-100"}`}>
          <List className="h-4 w-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Ärenden</span>
        </Link>
        {isAdmin && (
          <>
            <Link to="/admin/papperskorg" data-testid="nav-trash-btn" onClick={confirmNavigation}
                  aria-label="Papperskorg"
                  className={`press-feedback px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center min-h-[40px] ${
                    isActive("/admin/papperskorg") ? "text-[#3B9EDA] bg-[#E6F1FB]" : "text-slate-600 hover:bg-slate-100"}`}>
              <Trash2 className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Papperskorg</span>
            </Link>
            <Link to="/admin" data-testid="nav-admin-btn" onClick={confirmNavigation}
                  aria-label="Admin"
                  className={`press-feedback px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center min-h-[40px] ${
                    isActive("/admin") && loc.pathname === "/admin" ? "text-[#3B9EDA] bg-[#E6F1FB]" : "text-slate-600 hover:bg-slate-100"}`}>
              <Settings className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Admin</span>
            </Link>
          </>
        )}
        <div className="w-px h-6 bg-slate-200 mx-1 hidden sm:block" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-slate-700 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100 transition focus:outline-none focus:ring-2 focus:ring-[#3B9EDA]/40"
              aria-label={`Användarmeny – ${user?.name || user?.username || ""}`}
            >
              <UserCircle2 className="h-4 w-4" />
              <span className="hidden sm:inline">Inloggad som</span>
              <strong className="hidden sm:inline text-slate-800">{user?.name || user?.username}</strong>
              {isAdmin && <span className="text-indigo-600 font-semibold hidden sm:inline">· admin</span>}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-content">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col">
                <span className="font-semibold text-slate-800">{user?.name || user?.username}</span>
                <span className="text-xs text-slate-500">
                  {user?.username}{isAdmin ? " · admin" : " · tekniker"}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="menu-change-pin"
              onSelect={(e) => { e.preventDefault(); setPinOpen(true); }}
              className="cursor-pointer"
            >
              <KeyRound className="h-4 w-4 mr-2 text-[#3B9EDA]" />
              {pinLabel}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="menu-switch-user"
              onSelect={(e) => { e.preventDefault(); handleSwitchUser(); }}
              className="cursor-pointer"
            >
              <Repeat className="h-4 w-4 mr-2 text-slate-500" />
              Byt användare
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="menu-logout"
              onSelect={(e) => { e.preventDefault(); logout(); }}
              className="cursor-pointer text-red-600 focus:text-red-700"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logga ut
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Keep an always-visible logout button as a safety fallback */}
        <Button data-testid="btn-logout" variant="outline" size="sm"
                className="rounded-full hidden sm:inline-flex"
                onClick={logout}>
          <LogOut className="h-3.5 w-3.5 mr-1" /> Logga ut
        </Button>
      </nav>

      <ChangePinDialog open={pinOpen} onOpenChange={setPinOpen} />
    </header>
  );
};

export default Header;
